import React from 'react';
import { useTranslation } from 'react-i18next';
import DashboardWidgetShell from './DashboardWidgetShell';

const toCount = (value) => Math.max(0, Math.round(Number(value) || 0));

// Share in percent, null when the base is zero (no division by zero, shown as "-").
export const safeRate = (part, base) => {
  const denominator = toCount(base);
  if (denominator === 0) return null;
  return Math.round((toCount(part) / denominator) * 1000) / 10;
};

const Stat = ({ label, value, testId }) => (
  <div className="min-w-0" data-testid={testId}>
    <p className="text-2xl font-semibold text-gray-900 font-sans">{value}</p>
    <p className="mt-1 text-xs text-gray-500 font-sans">{label}</p>
  </div>
);

/**
 * Widget funnel for the workspace dashboard: impressions -> opens -> conversations,
 * conversion open -> conversation and error share. Data comes from widget_events_daily.
 * funnel = null (collection missing, no rows) -> nothing is rendered.
 */
const FunnelKpiWidget = ({ funnel, dateRange = 30 }) => {
  const { t } = useTranslation();
  if (!funnel) return null;

  const impressions = toCount(funnel.impressions);
  const opens = toCount(funnel.opens);
  const conversations = toCount(funnel.firstMessages);
  const errors = toCount(funnel.errors);
  const conversion = safeRate(conversations, opens);
  const errorShare = safeRate(errors, opens);
  const formatRate = (rate) => (rate == null ? '-' : `${rate.toLocaleString()}%`);

  const title = t('dashboard.agent.funnel.title', { defaultValue: 'Widget funnel' });
  const subtitle = t('dashboard.agent.funnel.subtitle', { days: dateRange, defaultValue: `Last ${dateRange} days` });

  if (impressions === 0) {
    return (
      <DashboardWidgetShell title={title} subtitle={subtitle}>
        <p className="text-sm text-amber-700 font-sans" data-testid="funnel-invisible">
          {t('dashboard.agent.funnel.invisible', {
            defaultValue: 'The chat bubble was not visible on your site in this period (0 impressions). Check that the widget script is still installed.'
          })}
        </p>
      </DashboardWidgetShell>
    );
  }

  return (
    <DashboardWidgetShell
      title={title}
      subtitle={subtitle}
      footer={(
        <p className="text-xs text-gray-400 font-sans">
          {t('dashboard.agent.funnel.cookieNote', {
            defaultValue: 'On mobile, impressions may be lower where a cookie banner covers the bubble.'
          })}
        </p>
      )}
    >
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Stat testId="funnel-impressions" label={t('dashboard.agent.funnel.impressions', { defaultValue: 'Impressions' })} value={impressions.toLocaleString()} />
        <Stat testId="funnel-opens" label={t('dashboard.agent.funnel.opens', { defaultValue: 'Opens' })} value={opens.toLocaleString()} />
        <Stat testId="funnel-conversations" label={t('dashboard.agent.funnel.conversations', { defaultValue: 'Conversations' })} value={conversations.toLocaleString()} />
        <Stat testId="funnel-conversion" label={t('dashboard.agent.funnel.conversion', { defaultValue: 'Open to conversation' })} value={formatRate(conversion)} />
      </div>
      {errors > 0 && (
        <p className="mt-3 text-xs text-gray-500 font-sans" data-testid="funnel-errors">
          {t('dashboard.agent.funnel.errors', {
            errorCount: errors,
            share: formatRate(errorShare),
            defaultValue: `Send errors: ${errors} (${formatRate(errorShare)} of opens)`
          })}
        </p>
      )}
    </DashboardWidgetShell>
  );
};

export default FunnelKpiWidget;
